import { useState, useEffect, useRef } from 'react'
import { router, Head } from '@inertiajs/react'
import { SlidersHorizontal, Search, Users, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import PersonCard from '@/components/PersonCard'
import OrgCard from '@/components/OrgCard'
import EventPagination from '@/components/EventPagination'
import type { NetworkPerson, NetworkOrg, PaginationMeta } from '@/types'

type Filters = {
  q?: string
  cause?: string
  city?: string
  state?: string
  industry?: string
}

type Props = {
  recommended_people: NetworkPerson[]
  recommended_organizations: NetworkOrg[]
  people: NetworkPerson[]
  organizations: NetworkOrg[]
  tab: 'people' | 'orgs'
  pagination: PaginationMeta
  filters: Filters
  causes: string[]
  industries: string[]
}

export default function NetworkIndex({
  recommended_people,
  recommended_organizations,
  people,
  organizations,
  tab,
  pagination,
  filters,
  causes,
  industries,
}: Props) {
  const [filterState, setFilterState] = useState<Filters>(filters)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const isInitialMount = useRef(true)
  const skipNextFilterRequest = useRef(false)

  function updateFilter(key: keyof Filters, value: string) {
    setFilterState((prev) => ({ ...prev, [key]: value }))
  }

  function filtersForTab(nextTab: string, source: Filters = filterState): Filters {
    if (nextTab !== 'orgs') return source

    const { city: _city, state: _state, ...rest } = source
    return rest
  }

  function queryParams(overrides: Record<string, string> = {}, source: Filters = filterState) {
    const nextTab = overrides.tab || tab
    const scopedFilters = filtersForTab(nextTab, source)

    return Object.fromEntries(
      Object.entries({ ...scopedFilters, tab, ...overrides }).filter(([, v]) => v && v !== 'all')
    )
  }

  function applyFilters() {
    router.get('/network', queryParams(), { preserveState: true, preserveScroll: true })
  }

  function changeTab(next: string) {
    const nextFilters = filtersForTab(next)
    if (nextFilters !== filterState) {
      skipNextFilterRequest.current = true
      setFilterState(nextFilters)
    }
    router.get('/network', queryParams({ tab: next }, nextFilters), { preserveState: true, preserveScroll: true })
  }

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (skipNextFilterRequest.current) {
      skipNextFilterRequest.current = false
      return
    }
    const timer = setTimeout(() => {
      router.get('/network', queryParams(), { preserveState: true, preserveScroll: true, replace: true })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState])

  function clearFilters() {
    skipNextFilterRequest.current = true
    setFilterState({})
    router.get('/network', { tab })
  }

  const hasActiveFilters = Object.values(filterState).some((v) => v && v !== 'all')
  const hasAdvancedFilters = Object.entries(filterState).some(([k, v]) => k !== 'q' && v && v !== 'all')
  const showRecommendations =
    !hasActiveFilters && (recommended_people.length > 0 || recommended_organizations.length > 0)

  return (
    <>
      <Head title="Network" />

      {/* Header anchor */}
      <section className="relative overflow-hidden border-b border-black/5">
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute inset-x-0 top-0 h-[140%] bg-[linear-gradient(to_bottom,oklch(0.85_0.11_62/0.55)_0%,oklch(0.91_0.07_72/0.28)_28%,transparent_62%)]" />
          <div className="absolute -top-1/4 -right-[8%] h-[95%] w-[60%] rounded-full bg-[radial-gradient(circle,oklch(0.80_0.15_55/0.45)_0%,transparent_65%)] blur-2xl" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
            <span aria-hidden="true">✦</span>
            Your network
          </p>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
            Find people and organizations to{' '}
            <span className="font-serif italic text-primary underline decoration-primary/30 decoration-4 underline-offset-4">
              connect
            </span>{' '}
            with.
          </h1>
          <p className="mt-4 max-w-[52ch] text-lg text-foreground text-pretty">
            Discover fellow philanthropists and causes that align with your interests, and grow your giving community.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Recommended */}
        {showRecommendations && (
          <div className="mb-12 space-y-10">
            {recommended_people.length > 0 && (
              <section>
                <h2 className="mb-1 text-2xl font-semibold tracking-tight">People you may know</h2>
                <p className="mb-5 text-base text-muted-foreground">Philanthropists who share your causes and networks</p>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {recommended_people.map((person) => (
                    <PersonCard key={person.id} person={person} />
                  ))}
                </div>
              </section>
            )}
            {recommended_organizations.length > 0 && (
              <section>
                <h2 className="mb-1 text-2xl font-semibold tracking-tight">Organizations for you</h2>
                <p className="mb-5 text-base text-muted-foreground">Causes aligned with your interests</p>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {recommended_organizations.map((org) => (
                    <OrgCard key={org.id} org={org} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Directory */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Browse all</h2>
            <p className="mt-1 text-base text-muted-foreground">{pagination.count} {tab === 'orgs' ? 'organizations' : 'people'}</p>
          </div>
          <Tabs value={tab} onValueChange={changeTab}>
            <TabsList>
              <TabsTrigger value="people"><Users className="size-4" />People</TabsTrigger>
              <TabsTrigger value="orgs"><Building2 className="size-4" />Organizations</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={tab === 'orgs' ? 'Search organizations...' : 'Search people...'}
              value={filterState.q || ''}
              onChange={(e) => updateFilter('q', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={applyFilters}>Search</Button>
          <Button variant={hasAdvancedFilters ? 'secondary' : 'outline'} size="sm" onClick={() => setFiltersOpen(!filtersOpen)}>
            <SlidersHorizontal className="size-4" />Filters
          </Button>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
        </div>

        {filtersOpen && (
          <div className="mb-8 space-y-4 rounded-xl bg-muted/50 p-4">
            <div className={tab === 'people' ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
              <Select value={filterState.cause || 'all'} onValueChange={(v) => updateFilter('cause', v)}>
                <SelectTrigger><SelectValue placeholder="All causes" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All causes</SelectItem>
                    {[...causes].sort().map((cause) => <SelectItem key={cause} value={cause}>{cause}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Select value={filterState.industry || 'all'} onValueChange={(v) => updateFilter('industry', v)}>
                <SelectTrigger><SelectValue placeholder="All professional networks" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All professional networks</SelectItem>
                    {[...industries].sort().map((industry) => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {tab === 'people' && (
                <>
                  <Input placeholder="City" value={filterState.city || ''} onChange={(e) => updateFilter('city', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} />
                  <Input placeholder="State" value={filterState.state || ''} onChange={(e) => updateFilter('state', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyFilters()} />
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'people' ? (
          people.length === 0 ? (
            <EmptyState label="people" onClear={hasActiveFilters ? clearFilters : undefined} />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 pb-4 sm:grid-cols-2 lg:grid-cols-3">
                {people.map((person) => <PersonCard key={person.id} person={person} />)}
              </div>
              <EventPagination pagination={pagination} baseUrl="/network" filters={{ ...filterState, tab }} />
            </>
          )
        ) : organizations.length === 0 ? (
          <EmptyState label="organizations" onClear={hasActiveFilters ? clearFilters : undefined} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 pb-4 sm:grid-cols-2 lg:grid-cols-3">
              {organizations.map((org) => <OrgCard key={org.id} org={org} />)}
            </div>
            <EventPagination pagination={pagination} baseUrl="/network" filters={{ ...filterState, tab }} />
          </>
        )}
      </div>
    </>
  )
}

function EmptyState({ label, onClear }: { label: string; onClear?: () => void }) {
  return (
    <div className="py-20 text-center">
      <h2 className="text-2xl font-semibold tracking-tight">No {label} match your filters</h2>
      <p className="mt-2 text-muted-foreground">Try adjusting your search or clearing filters.</p>
      {onClear && <Button variant="outline" onClick={onClear} className="mt-6">Clear all filters</Button>}
    </div>
  )
}
